import {
  collection, doc, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { SCHEMA_VERSION } from './dataExport';

// Imports a JSON file produced by dataExport.collectAll(). Always creates
// NEW boards (no merge / no overwrite) so an import can't clobber existing
// data and so re-imports stack cleanly.
//
// Behaviour:
//   • Validates `schemaVersion === SCHEMA_VERSION` (currently 1).
//   • Strips share state (editors/viewers/shareToken/shareTokenRole) — those
//     belong to the source account and don't carry across.
//   • Re-keys list IDs and remaps reminder.sourceListId via an
//     old→new map. Reminders whose source list is missing are dropped.
//   • Uses serverTimestamp() for createdAt/updatedAt; preserves
//     parsedDate as an actual JS Date so Firestore stores a Timestamp.
//
// Returns a summary { boardsCreated, listsCreated, remindersCreated,
//                    remindersSkipped }.

export class ImportError extends Error {
  constructor(message, code) {
    super(message);
    this.code = code;
  }
}

export function parseImportText(text) {
  let payload;
  try {
    payload = JSON.parse(text);
  } catch (_e) {
    throw new ImportError('Not a valid JSON file.', 'invalid_json');
  }
  if (!payload || typeof payload !== 'object') {
    throw new ImportError('Import file is empty or malformed.', 'bad_shape');
  }
  if (payload.schemaVersion !== SCHEMA_VERSION) {
    throw new ImportError(
      `Unsupported schema version (${payload.schemaVersion}). Expected ${SCHEMA_VERSION}.`,
      'schema_mismatch',
    );
  }
  if (!Array.isArray(payload.boards)) {
    throw new ImportError('Import file is missing the `boards` list.', 'no_boards');
  }
  return payload;
}

// Quick preview counts so the UI can prompt "import N boards, M lists,
// K reminders?" before doing any writes.
export function summarisePayload(payload) {
  const boards = payload.boards.length;
  let lists = 0;
  for (const b of payload.boards) {
    if (Array.isArray(b.lists)) lists += b.lists.length;
  }
  const reminders = Array.isArray(payload.reminders) ? payload.reminders.length : 0;
  return { boards, lists, reminders };
}

export async function importIntoUser(user, payload) {
  if (!user?.uid) throw new ImportError('No signed-in user.', 'no_user');

  // Pre-allocate doc refs so we can wire the old→new list-id map BEFORE
  // committing anything. Then write boards + lists in batched chunks
  // (Firestore caps each batch at 500 ops) so a big import doesn't blow
  // up on one giant batch.
  const oldToNewListId = new Map();
  const plannedBoards = []; // { ref, data, lists: [{ ref, data, oldId }] }

  for (const board of payload.boards) {
    const boardRef = doc(collection(db, 'users', user.uid, 'boards'));
    const boardData = {
      name: typeof board.name === 'string' ? board.name : 'imported board',
      mode: board.mode === 'grid' ? 'grid' : 'free',
      ownerId: user.uid,
      editors: [],
      viewers: [],
      shareToken: null,
      shareTokenRole: 'viewer',
      // Deliberately NOT setting `order` on imports. useBoards' sort
      // puts boards-with-`order` first and boards-without-`order` last
      // (by createdAt). Imports get a fresh serverTimestamp createdAt,
      // so they land at the very end of the sidebar, after both any
      // ordered boards AND any pre-existing without-order legacy
      // boards. Source `order` from the export is also dropped.
      createdAt: serverTimestamp(),
    };
    if (boardData.mode === 'grid' && typeof board.gridColumnCount === 'number') {
      boardData.gridColumnCount = board.gridColumnCount;
    }

    const lists = [];
    for (const list of (Array.isArray(board.lists) ? board.lists : [])) {
      const listRef = doc(collection(db, 'users', user.uid, 'boards', boardRef.id, 'lists'));
      if (typeof list.id === 'string' && list.id) {
        oldToNewListId.set(list.id, listRef.id);
      }
      const listData = {
        title: typeof list.title === 'string' ? list.title : '',
        content: typeof list.content === 'string' ? list.content : '',
        x: numOrDefault(list.x, 80),
        y: numOrDefault(list.y, 60),
        w: numOrDefault(list.w, 280),
        h: numOrDefault(list.h, 320),
        z: numOrDefault(list.z, 1),
        order: numOrDefault(list.order, 0),
        state: list.state === 'archived' ? 'archived' : 'open',
        gridCol: numOrDefault(list.gridCol, 0),
        gridRow: numOrDefault(list.gridRow, 0),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      lists.push({ ref: listRef, data: listData });
    }
    plannedBoards.push({ ref: boardRef, data: boardData, lists });
  }

  // Plan reminders only after we have the old→new map populated.
  const plannedReminders = []; // { ref, data }
  let remindersSkipped = 0;
  for (const reminder of (Array.isArray(payload.reminders) ? payload.reminders : [])) {
    const oldId = reminder.sourceListId;
    const newId = oldId ? oldToNewListId.get(oldId) : null;
    if (oldId && !newId) {
      // Source list isn't in this import — skip rather than orphan.
      remindersSkipped += 1;
      continue;
    }
    const remRef = doc(collection(db, 'users', user.uid, 'reminders'));
    const remData = {
      text: typeof reminder.text === 'string' ? reminder.text : '',
      parsedDate: parseISODateOrNull(reminder.parsedDate),
      recurrence: reminder.recurrence || null,
      sourceListId: newId || null,
      sourceLineHash: typeof reminder.sourceLineHash === 'string' ? reminder.sourceLineHash : null,
      status: reminder.status === 'done' || reminder.status === 'snoozed' ? reminder.status : 'open',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    plannedReminders.push({ ref: remRef, data: remData });
  }

  // Commit in chunks of 400 ops (under Firestore's 500/batch cap).
  const allWrites = [];
  for (const b of plannedBoards) {
    allWrites.push({ ref: b.ref, data: b.data });
    for (const l of b.lists) allWrites.push({ ref: l.ref, data: l.data });
  }
  for (const r of plannedReminders) allWrites.push({ ref: r.ref, data: r.data });

  let i = 0;
  while (i < allWrites.length) {
    const batch = writeBatch(db);
    const slice = allWrites.slice(i, i + 400);
    for (const w of slice) batch.set(w.ref, w.data);
    await batch.commit();
    i += 400;
  }

  return {
    boardsCreated: plannedBoards.length,
    listsCreated: plannedBoards.reduce((n, b) => n + b.lists.length, 0),
    remindersCreated: plannedReminders.length,
    remindersSkipped,
  };
}

function numOrDefault(v, d) {
  return typeof v === 'number' && Number.isFinite(v) ? v : d;
}

function parseISODateOrNull(v) {
  if (!v) return null;
  if (typeof v !== 'string') return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}
